import { useEffect, useState } from "react";
import classNames from "classnames";
import WhatsappIcon from "@/assets/Icons/whatsapp.svg?react";
import HeadsetIcon from "@/assets/Icons/headset.svg?react";
import { PhoneIcon } from "@heroicons/react/24/outline";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { useTranslation } from "react-i18next";

interface SupportButtonProps {
  isSendolaBusiness?: boolean;
}

export const SupportButton = ({
  isSendolaBusiness = false,
}: SupportButtonProps) => {
  const [t] = useTranslation("global");
  const [open, setOpen] = useState<boolean>(false);
  const [hoverTel, setHoverTel] = useState<boolean>(false);
  const [hoverSupport, setHoverSupport] = useState<boolean>(false);
  const [toolTipOffset, setToolTipOffset] = useState<number>(0);
  const [toolTipText, setToolTipText] = useState<string>("");
  const [visible, setVisible] = useState<boolean>(true);

  const ref = useOutsideClick(() => {
    if (open) setOpen(false);
  });

  useEffect(() => {
    const updateScrollDirection = () => {
      const scrollY = window.scrollY + window.innerHeight;
      const scrollHeight = document.body.scrollHeight;

      if (scrollY + 50 > scrollHeight) {
        setOpen(false);
        setVisible(false);
      } else {
        setVisible(true);
      }
    };

    window.addEventListener("scroll", updateScrollDirection);

    return () => {
      window.removeEventListener("scroll", updateScrollDirection);
    };
  }, []);

  return (
    <div
      className={classNames(
        "fixed right-6 bg-black/65 cursor-pointer rounded-full transition-transform z-50",
        {
          "-bottom-[2px]": !isSendolaBusiness,
          "bottom-14": isSendolaBusiness,
          "-translate-y-10": visible,
          "translate-y-28": !visible,
        }
      )}
    >
      <div
        className={classNames("relative flex flex-col w-14 transition-all", {
          "h-[182px]": open,
          "h-[54px]": !open,
        })}
      >
        <div
          style={{
            transform: `translate(${0}px, -${toolTipOffset}px)`,
            opacity: hoverTel || hoverSupport ? "100%" : "0%",
          }}
          className="hidden md:flex h-8 w-max transition-all rounded bg-black text-white absolute right-16 -bottom-[2px] px-4 items-center select-none"
        >
          <p className="text-sm">{toolTipText}</p>
        </div>

        <a
          href="tel:2816388018"
          onMouseEnter={() => {
            setToolTipOffset(140);
            setToolTipText(t("FloatingButton.Phone"));
            setHoverTel(true);
          }}
          onMouseLeave={() => setHoverTel(false)}
          className={classNames(
            "flex absolute items-center justify-center -bottom-[2px] w-14 h-14 bg-black hover:bg-gray-800 text-white rounded-full transition-all z-30",
            {
              "-translate-y-0": !open,
              "-translate-y-32": open,
            }
          )}
        >
          <PhoneIcon className="w-7 h-7" />
        </a>
        <a
          target="_blank"
          rel="noreferrer noopener"
          href="https://wa.me/message/QOIDOASIDF3EO1"
          onMouseEnter={() => {
            setToolTipOffset(75);
            setToolTipText(t("FloatingButton.Support"));
            setHoverSupport(true);
          }}
          onMouseLeave={() => setHoverSupport(false)}
          className={classNames(
            "flex absolute items-center justify-center -bottom-[2px] w-14 h-14 bg-black hover:bg-gray-800 text-white rounded-full transition-all z-40",
            {
              "-translate-y-0": !open,
              "-translate-y-16": open,
            }
          )}
        >
          <WhatsappIcon className="w-7 h-7 p-[1px]" />
        </a>
        <div
          ref={ref}
          className={classNames(
            "flex absolute items-center justify-center -bottom-[2px] w-14 h-14 rounded-full z-50 transition-all",
            {
              "bg-black text-white border-[2px] border-gray-800": !open,
              "bg-white text-black border-[2px] border-gray-200": open,
            }
          )}
          onClick={() => setOpen(!open)}
        >
          <HeadsetIcon className="w-7 h-7" />
        </div>
      </div>
    </div>
  );
};
